import axios, { AxiosError, AxiosRequestConfig } from 'axios';

export const HUMIO_SET_THEME = (theme: string) => {
  return `
    mutation {
      updateSettings(uiTheme: ${theme}, featureAnnouncementsToDismiss: [PuffinRebranding, Interactions, FetchMoreOnFieldsPanel, FieldInteractions], isResizableQueryFieldMessageDismissed: true) {
        __typename
      }
    }
  `;
};

const capitalize = (text: string) => {
  if (text.length) {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }
  return '';
};

/**
 * Sets the Humio UI theme for the current user
 * @param {string} humioUrlPrefix - The URL prefix for the Humio instance
 * @param {string} theme - The theme to set (defaults to 'Dark')
 * @param {string} token - The Humio session token
 */
export const setHumioTheme = async (
  humioUrlPrefix: string,
  theme?: string,
  token?: string,
) => {
  if (!token || token === 'undefined' || token === 'null') {
    return;
  }

  if (!humioUrlPrefix) {
    return;
  }

  theme = theme ? capitalize(theme) : 'Dark';

  const humioConfig: AxiosRequestConfig = {
    method: 'post',
    url: `/${humioUrlPrefix}/graphql`,
    data: JSON.stringify({
      query: HUMIO_SET_THEME(theme),
    }),
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    timeout: 5000, // Add timeout to prevent hanging requests
    validateStatus: (status) => status < 500,
  };

  try {
    const response = await axios(humioConfig);

    // Check if the response indicates success
    if (response.status === 200) {
      return response.data;
    } else if (response.status === 404) {
      // Store flag to skip future attempts
      sessionStorage.setItem('humio_theme_unsupported', 'true');
      return null;
    } else {
      return null;
    }
  } catch (error) {
    const axiosError = error as AxiosError;

    // Handle different error scenarios
    if (axiosError.response?.status === 500) {
      // Store flag to skip future attempts for this session
      sessionStorage.setItem('humio_theme_unsupported', 'true');
    } else if (axiosError.response?.status === 401) {
      // Unauthorized - token might be invalid
    } else if (axiosError.code === 'ECONNABORTED') {
      // Request timeout
    }

    return null;
  }
};

/**
 * Check if theme setting is supported for this Humio instance
 */
export const isHumioThemeSupported = (): boolean => {
  return sessionStorage.getItem('humio_theme_unsupported') !== 'true';
};
